import React, { useEffect, useMemo, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import Alert from "../../components/Alert";
import { createLookupApi } from "../../api/lookupApi";
import { listStaff, updateStaff } from "../../api/staffApi";
import { listTeacherSections } from "../../api/teacherSectionApi";
import "../Attendance/Attendance.css";

const designationApi = createLookupApi("designations");

const GENDERS = ["Male", "Female"];
const RELIGIONS = ["Islam", "Hinduism", "Buddist", "Christian", "Other"];
const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-", "N/A"];

/**
 * Direct port of digicampusbd's "HRM Info Update" (`staff-multiple-update`,
 * template `staff-multiple-update.html`) — an editable grid over every real
 * staff member letting several be bulk-updated in one submit. The old page
 * used a hand-rolled DataTables grid where every cell was its own
 * input/select and a single PUT sent the whole batch; here the same result
 * is reached with React state per row plus the *already-existing*
 * `PUT /api/staff/{id}` (same endpoint `StaffFormPage`'s single-record edit
 * already uses) called once per checked row — no new backend endpoint
 * needed, since a batch of single updates is externally identical to a
 * dedicated bulk endpoint and this reuses already-verified update logic
 * instead of duplicating it. Not ported: the old page's per-column
 * show/hide toggle (`colvis`) — this app's established `.table-scroll`
 * horizontal-scroll pattern (used on every other wide table) gives the same
 * "see all the fields" capability without that extra control.
 */
export default function StaffMultipleUpdatePage() {
  const [staffList, setStaffList] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [teacherSections, setTeacherSections] = useState([]);
  const [edits, setEdits] = useState({});
  const [selected, setSelected] = useState({});
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadStaff = () => {
    setLoading(true);
    setError("");
    listStaff()
      .then(setStaffList)
      .catch(() => setError("Failed to load staff list."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadStaff();
    Promise.all([designationApi.list(), listTeacherSections()]).then(([d, t]) => {
      setDesignations(d);
      setTeacherSections(t);
    });
  }, []);

  const rows = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return staffList;
    return staffList.filter((s) => (s.customStaffId || "").toLowerCase().includes(needle));
  }, [staffList, filter]);

  const fieldValue = (row, field) => {
    const rowEdits = edits[row.staffId];
    if (rowEdits && field in rowEdits) return rowEdits[field];
    return row[field] == null ? "" : row[field];
  };

  const setField = (staffId, field, value) => {
    setEdits((prev) => ({ ...prev, [staffId]: { ...prev[staffId], [field]: value } }));
  };

  const toggleRow = (staffId) => {
    setSelected((prev) => ({ ...prev, [staffId]: !prev[staffId] }));
  };

  const toggleAll = (checked) => {
    const next = {};
    rows.forEach((r) => (next[r.staffId] = checked));
    setSelected(next);
  };

  const allChecked = rows.length > 0 && rows.every((r) => selected[r.staffId]);

  const handleUpdate = async () => {
    const targets = rows.filter((r) => selected[r.staffId]);
    if (targets.length === 0) {
      setError("Please select at least one staff member.");
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await Promise.all(
        targets.map((row) => {
          const payload = {
            customStaffId: fieldValue(row, "customStaffId"),
            staffName: fieldValue(row, "staffName"),
            nickName: fieldValue(row, "nickName"),
            staffSerial: fieldValue(row, "staffSerial") ? Number(fieldValue(row, "staffSerial")) : null,
            gender: fieldValue(row, "gender"),
            religion: fieldValue(row, "religion"),
            staffStatus: Number(fieldValue(row, "staffStatus")),
            staffAddress: fieldValue(row, "staffAddress"),
            fatherName: fieldValue(row, "fatherName"),
            motherName: fieldValue(row, "motherName"),
            mobileNumber: fieldValue(row, "mobileNumber"),
            email: fieldValue(row, "email"),
            bloodGroup: fieldValue(row, "bloodGroup"),
            birthDate: fieldValue(row, "birthDate") || null,
            employmentDate: fieldValue(row, "employmentDate") || null,
            resignDate: fieldValue(row, "resignDate") || null,
            designationId: fieldValue(row, "designationId") ? Number(fieldValue(row, "designationId")) : null,
            teacherSectionId: fieldValue(row, "teacherSectionId") ? Number(fieldValue(row, "teacherSectionId")) : null,
            qualification: fieldValue(row, "qualification"),
            staffNid: fieldValue(row, "staffNid"),
            subjectExpertise: fieldValue(row, "subjectExpertise"),
            indexNo: fieldValue(row, "indexNo"),
            appointingAuthority: fieldValue(row, "appointingAuthority"),
          };
          return updateStaff(row.staffId, payload);
        })
      );
      setSuccess(`${targets.length} staff record(s) updated.`);
      setEdits({});
      setSelected({});
      loadStaff();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to update one or more staff records.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Staff Multiple Update</h1>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      <div className="attendance-filters">
        <label>
          Search by Staff ID
          <input type="text" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search by Staff ID" />
        </label>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <div className="table-scroll">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>
                    <input type="checkbox" checked={allChecked} onChange={(e) => toggleAll(e.target.checked)} />
                  </th>
                  <th>Staff ID</th>
                  <th>Name</th>
                  <th>Nick Name</th>
                  <th>Serial</th>
                  <th>Designation</th>
                  <th>Section</th>
                  <th>Mobile No.</th>
                  <th>Email</th>
                  <th>Father</th>
                  <th>Mother</th>
                  <th>Birthdate</th>
                  <th>Joining Date</th>
                  <th>Resign Date</th>
                  <th>Gender</th>
                  <th>Religion</th>
                  <th>Blood Group</th>
                  <th>Status</th>
                  <th>Address</th>
                  <th>Appointing Authority</th>
                  <th>Index No</th>
                  <th>Subject Expertise</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.staffId}>
                    <td>
                      <input type="checkbox" checked={!!selected[row.staffId]} onChange={() => toggleRow(row.staffId)} />
                    </td>
                    <td>
                      <input style={{ width: 90 }} value={fieldValue(row, "customStaffId")} onChange={(e) => setField(row.staffId, "customStaffId", e.target.value)} />
                    </td>
                    <td>
                      <input style={{ width: 150 }} value={fieldValue(row, "staffName")} onChange={(e) => setField(row.staffId, "staffName", e.target.value)} />
                    </td>
                    <td>
                      <input style={{ width: 120 }} value={fieldValue(row, "nickName")} onChange={(e) => setField(row.staffId, "nickName", e.target.value)} />
                    </td>
                    <td>
                      <input style={{ width: 70 }} type="number" value={fieldValue(row, "staffSerial")} onChange={(e) => setField(row.staffId, "staffSerial", e.target.value)} />
                    </td>
                    <td>
                      <select value={fieldValue(row, "designationId")} onChange={(e) => setField(row.staffId, "designationId", e.target.value)}>
                        <option value="">Select Designation</option>
                        {designations.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select value={fieldValue(row, "teacherSectionId")} onChange={(e) => setField(row.staffId, "teacherSectionId", e.target.value)}>
                        <option value="">Select Section</option>
                        {teacherSections.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input style={{ width: 120 }} value={fieldValue(row, "mobileNumber")} onChange={(e) => setField(row.staffId, "mobileNumber", e.target.value)} />
                    </td>
                    <td>
                      <input style={{ width: 160 }} value={fieldValue(row, "email")} onChange={(e) => setField(row.staffId, "email", e.target.value)} />
                    </td>
                    <td>
                      <input style={{ width: 120 }} value={fieldValue(row, "fatherName")} onChange={(e) => setField(row.staffId, "fatherName", e.target.value)} />
                    </td>
                    <td>
                      <input style={{ width: 120 }} value={fieldValue(row, "motherName")} onChange={(e) => setField(row.staffId, "motherName", e.target.value)} />
                    </td>
                    <td>
                      <input style={{ width: 140 }} type="date" value={fieldValue(row, "birthDate")} onChange={(e) => setField(row.staffId, "birthDate", e.target.value)} />
                    </td>
                    <td>
                      <input style={{ width: 140 }} type="date" value={fieldValue(row, "employmentDate")} onChange={(e) => setField(row.staffId, "employmentDate", e.target.value)} />
                    </td>
                    <td>
                      <input style={{ width: 140 }} type="date" value={fieldValue(row, "resignDate")} onChange={(e) => setField(row.staffId, "resignDate", e.target.value)} />
                    </td>
                    <td>
                      <select value={fieldValue(row, "gender")} onChange={(e) => setField(row.staffId, "gender", e.target.value)}>
                        {GENDERS.map((g) => (
                          <option key={g} value={g}>
                            {g}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select value={fieldValue(row, "religion")} onChange={(e) => setField(row.staffId, "religion", e.target.value)}>
                        <option value="">Select</option>
                        {RELIGIONS.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select value={fieldValue(row, "bloodGroup")} onChange={(e) => setField(row.staffId, "bloodGroup", e.target.value)}>
                        <option value="">Select</option>
                        {BLOOD_GROUPS.map((b) => (
                          <option key={b} value={b}>
                            {b}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select value={fieldValue(row, "staffStatus")} onChange={(e) => setField(row.staffId, "staffStatus", e.target.value)}>
                        <option value={1}>Active</option>
                        <option value={0}>Inactive</option>
                      </select>
                    </td>
                    <td>
                      <textarea style={{ width: 160 }} value={fieldValue(row, "staffAddress")} onChange={(e) => setField(row.staffId, "staffAddress", e.target.value)} />
                    </td>
                    <td>
                      <input style={{ width: 140 }} value={fieldValue(row, "appointingAuthority")} onChange={(e) => setField(row.staffId, "appointingAuthority", e.target.value)} />
                    </td>
                    <td>
                      <input style={{ width: 100 }} value={fieldValue(row, "indexNo")} onChange={(e) => setField(row.staffId, "indexNo", e.target.value)} />
                    </td>
                    <td>
                      <input style={{ width: 140 }} value={fieldValue(row, "subjectExpertise")} onChange={(e) => setField(row.staffId, "subjectExpertise", e.target.value)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {rows.length === 0 && <p>No staff found.</p>}

          <div className="form-actions">
            <button type="button" className="btn-primary" onClick={handleUpdate} disabled={saving || rows.length === 0}>
              {saving ? "Updating..." : "Update"}
            </button>
          </div>
          <p className="mendatory">
            Check the row(s) you want to update, edit any fields inline, then click Update. Unedited fields are saved as-is.
          </p>
        </>
      )}
    </MainLayout>
  );
}
